<template>
  <div class="w-full max-w-md p-8">
    <h1 class="font-[700] text-3xl text-center text-title mb-[1vh]">{{
      $t('pages.signup.step3.title') }}</h1>
    <p class="font-[600] text-base text-center align-middle text-body mb-[2vh]">
      {{ $t('pages.signup.step3.subtitle') }}
    </p>

    <UForm method="post" :state="formState" :schema="schema" @submit="onSubmit" class="space-y-4">
      <UFormField name="name" label="Name" class="h-24">
        <UInput class="w-full" size="xl" v-model="formState.name" type="text"
          :placeholder="$t('pages.signup.step3.form.name.placeholder')" :disabled="isLoading"
          :ui="{ base: 'py-3 px-4' }" />
      </UFormField>

      <UFormField name="password" label="Password" class="h-24">
        <UInput
          v-model="formState.password"
          :type="isPasswordVisible ? 'text' : 'password'"
          class="w-full"
          size="xl"
          :placeholder="$t('pages.signup.step3.form.password.placeholder')"
          :disabled="isLoading"
          :ui="{ trailing: 'pr-3', base: 'py-3 px-4' }"
        >
          <template v-if="formState.password" #trailing>
            <UIcon
              :name="isPasswordVisible ? 'i-lucide-eye' : 'i-lucide-eye-off'"
              class="w-5 h-5"
              @click="isPasswordVisible = !isPasswordVisible"
            />
          </template>
        </UInput>
      </UFormField>

      <UFormField name="passwordConfirm" label="Confirm Password" class="h-24">
        <UInput
          v-model="formState.passwordConfirm"
          :type="isPasswordVisible ? 'text' : 'password'"
          class="w-full"
          size="xl"
          :placeholder="$t('pages.signup.step3.form.passwordConfirm.placeholder')"
          :disabled="isLoading"
          :ui="{ base: 'py-3 px-4' }"
        />
      </UFormField>

      <UFormField v-if="showInvitationInput" name="invitation" label="Invitation Code" class="h-24">
        <UInput class="w-full" size="xl" v-model="formState.invitation" type="text"
          :placeholder="$t('pages.signup.step3.form.invitation.placeholder')" :disabled="isLoading"
          :ui="{ base: 'py-3 px-4' }" />
      </UFormField>

      <UButton type="submit" :disabled="!isHydrated" color="primary" size="xl" block :loading="isLoading" class="py-3">
        {{ $t('pages.signup.step3.continue') }}
      </UButton>
    </UForm>
  </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import { useMounted } from '@vueuse/core';
import * as yup from 'yup';
import { getConfigsApi } from '~/api/app';
import { registerApi } from '~/api/auth';
import { useAuth } from '~/composables/useAuth';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';
import { useLocalePath } from '#i18n';

const { t } = useI18n();
const { login } = useAuth();
const localePath = useLocalePath();
const query = useRoute().query;
const toastStore = useToastStore();
const inviteCode = query.invite_code as string;
const isLoading = ref(false);
// hydration 完成前不讓表單送得出去，避免瀏覽器走原生提交把欄位帶進網址列
const isHydrated = useMounted();
const isPasswordVisible = ref(false);

const props = defineProps<{
  code: string;
  email: string;
}>();

type FormState = {
  name: string;
  password: string;
  passwordConfirm: string;
  invitation?: string;
};

const formState = ref<FormState>({
  name: '',
  password: '',
  passwordConfirm: '',
  invitation: '',
});

// 動態創建驗證 schema，根據是否需要邀請碼來決定 invitation 欄位是否必填
const createSchema = (requiresInvitation: boolean) => {
  const baseSchema = {
    name: yup.string().required(t('pages.signup.step3.validation.name')),
    password: yup
      .string()
      .required(t('pages.signup.step3.form.password.required'))
      .max(20, t('pages.signup.step3.form.password.maxLength'))
      .min(8, t('pages.signup.step3.form.password.minLength'))
      .matches(/[a-zA-Z]/, t('pages.signup.step3.form.password.letter'))
      .matches(/[0-9]/, t('pages.signup.step3.form.password.number'))
      .test('safe-characters', t('pages.signup.step3.form.password.characters'), (value) => {
        if (!value) return true;
        const allowedPattern = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{}|;:,.<>?]*$/;
        return allowedPattern.test(value);
      }),
    passwordConfirm: yup
      .string()
      .oneOf(
        [yup.ref('password')],
        t('pages.signup.step3.validation.passwordConfirmMismatch'),
      ),
  };

  // 如果需要邀請碼，則設為必填；否則設為可選
  if (requiresInvitation) {
    return yup.object({
      ...baseSchema,
      invitation: yup.string().required(t('pages.signup.step3.validation.invitation')),
    });
  }
  return yup.object({
    ...baseSchema,
    invitation: yup.string().optional(),
  });
};

const schema = computed(() => createSchema(showInvitationInput.value));

async function onSubmit(event: FormSubmitEvent<FormState>) {
  isLoading.value = true;
  try {
    const payload = {
      code: props.code,
      email: props.email,
      invite_code: event.data.invitation || '', // 如果沒有邀請碼，傳空字串
      nickname: event.data.name,
      password: event.data.password,
    };

    const response = await registerApi(payload);
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    }

    const queryString = queryParams.toString();
    const redirectPath = queryString ? `/organization/setup?${queryString}` : '/organization/setup';

    await login(response.token, response.refresh_token, localePath(redirectPath));
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;
    if ('message' in apiError && apiError.message) {
      toastStore.error(apiError.message);
      return;
    }
    toastStore.error(t('pages.signup.step3.invitationCodeError'));
  } finally {
    isLoading.value = false;
  }
}

const showInvitationInput = ref(true);

// 獲取系統配置，判斷是否需要邀請碼
const loadConfig = async () => {
  try {
    const config = await getConfigsApi();
    // 如果 is_open_invite 為 true，表示需要邀請碼，顯示邀請碼欄位
    // 如果 is_open_invite 為 false，表示不需要邀請碼（開放註冊），隱藏邀請碼欄位
    showInvitationInput.value = config.is_open_invite;
  } catch (error) {
    // 如果 API 調用失敗，預設顯示邀請碼欄位（較安全的預設值）
    console.warn('無法載入系統配置，預設顯示邀請碼欄位:', error);
    showInvitationInput.value = true;
  }
};

onMounted(async () => {
  // 如果 URL 中有 invite_code 參數，則自動填入並隱藏輸入欄位
  if (inviteCode) {
    showInvitationInput.value = false;
    formState.value.invitation = inviteCode;
  } else {
    // 否則，從 API 獲取配置來決定是否顯示邀請碼欄位
    await loadConfig();
  }
});
</script>